import i18next from 'i18next';
import Moment from 'moment';
import { DateRange, extendMoment } from 'moment-range';
import React from 'react';

import { GlobalSearchIcon, PixelIcon } from '~/assets';
import { CustomerListIcon, SegmentsIcon } from '~/assets/audience';
import LookalikeIcon from '~/assets/audience/LookalikeIcon';
import PulseIcon from '~/assets/audience/PulseIcon';
import { AudienceNode } from '~/constants/graphqlTypes';
import { TaxonomyProperty } from '~/contexts/AudienceContext';
import {
  AudienceCondition,
  AudienceCustomerListInfo,
  AudienceFilterDateField,
  AudienceFilterNumberField,
  AudienceGlobalInfo,
  AudienceOverviewQuery,
  AudiencePixelStatus,
  AudiencePulseInfo,
  FilterExtendedStringField,
  FilterNumberField,
  FilterStringField,
  PersonFilterInput,
  PixelMatchingCadence,
  UtmGroup,
} from '~/types/api.graphql';

import { MatchTypeValue } from '../components/MatchTypeDropdown/MatchTypeDropdown';
import { visitIQTaxonomyUserSettings } from '../constants/localStorage';

// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
const moment = extendMoment(Moment as any);

export const allConditionsArray = [
  AudienceCondition.Pixel,
  AudienceCondition.CustomerList,
  AudienceCondition.Global,
  AudienceCondition.Segment,
  AudienceCondition.Lookalike,
  AudienceCondition.Pulse,
];

export const allPixelStatuses = [
  AudiencePixelStatus.Enabled,
  AudiencePixelStatus.Disabled,
  AudiencePixelStatus.QuotaReached,
  AudienceFilterNumberField.QuotaUsagePercentage,
  AudienceFilterDateField.LastNewMatchDate,
];

const conditionIconsMap = {
  [AudienceCondition.Pixel]: <PixelIcon />,
  [AudienceCondition.Global]: <GlobalSearchIcon />,
  [AudienceCondition.Segment]: <SegmentsIcon />,
  [AudienceCondition.CustomerList]: <CustomerListIcon />,
  [AudienceCondition.Lookalike]: <LookalikeIcon />,
  [AudienceCondition.Pulse]: <PulseIcon />,
};

export enum GroupKey {
  Custom = 'custom',
  B2B = 'b2b',
  ConsumerProfile = 'consumerProfile',
  Demographics = 'demographics',
  Household = 'household',
  Financial = 'financial',
}
type PropertyGroup = {
  key: GroupKey;
  label: string;
  properties: TaxonomyProperty[];
};

type AudienceInfoWithAdditions = AudiencePulseInfo | AudienceGlobalInfo | AudienceCustomerListInfo;

const PROPERTY_GROUPS_DEFINITIONS = [
  {
    key: GroupKey.Custom,
    label: 'Custom',
    properties: [], // Will be loaded from localStorage/user
  },
  {
    key: GroupKey.B2B,
    label: 'B2B',
    properties: [
      FilterStringField.JobTitle,
      FilterStringField.Department,
      FilterStringField.PrimaryIndustry,
      FilterStringField.CompanyState,
      FilterStringField.CompanyRevenue,
      FilterStringField.CompanyEmployeeCount,
    ],
  },
  {
    key: GroupKey.ConsumerProfile,
    label: 'Consumer Profile',
    properties: [
      FilterStringField.Interests,
      FilterStringField.Behaviors,
      FilterStringField.State,
      FilterStringField.Language,
      FilterStringField.Generation,
    ],
  },
  {
    key: GroupKey.Demographics,
    label: 'Demographics',
    properties: [
      FilterNumberField.Age,
      FilterStringField.Gender,
      FilterStringField.EthnicGroup,
      FilterStringField.Education,
      FilterStringField.MaritalStatus,
    ],
  },
  {
    key: GroupKey.Household,
    label: 'Household',
    properties: [
      FilterStringField.HomeOwner,
      FilterStringField.IncomeLevels,
      FilterStringField.NetWorth,
      FilterNumberField.PeopleInHousehold,
      FilterStringField.DwellingType,
    ],
  },
  {
    key: GroupKey.Financial,
    label: 'Financial',
    properties: [
      FilterStringField.CreditRange,
      FilterStringField.NetWorth,
      FilterNumberField.MortgageAmount,
      FilterStringField.NewCreditOfferedHousehold,
    ],
  },
];

export const conditionIcon = (condition: AudienceCondition) => conditionIconsMap[condition] || <GlobalSearchIcon />;

export const conditionTitle = (condition: AudienceCondition) =>
  i18next.exists(`specific:audience.overview.${condition}`)
    ? i18next.t(`specific:audience.overview.${condition}`)
    : condition;

type AudienceWithCondition = Pick<AudienceNode, 'condition' | 'parentAudience'>;

export const shouldShowPixelComponents = (audience?: AudienceWithCondition) =>
  audience?.condition === AudienceCondition.Pixel || audience?.parentAudience?.condition === AudienceCondition.Pixel;

export const shouldShowGlobalComponents = (audience?: AudienceWithCondition) =>
  audience?.condition === AudienceCondition.Global || audience?.parentAudience?.condition === AudienceCondition.Global;

export const shouldShowPulseComponents = (audience?: AudienceWithCondition) =>
  audience?.condition === AudienceCondition.Pulse || audience?.parentAudience?.condition === AudienceCondition.Pulse;

export const shouldShowCustomerListComponents = (audience?: AudienceWithCondition) =>
  audience?.condition === AudienceCondition.CustomerList ||
  audience?.parentAudience?.condition === AudienceCondition.CustomerList;

export const shouldShowAdditionsFilter = (audience?: AudienceWithCondition) =>
  shouldShowGlobalComponents(audience) || shouldShowCustomerListComponents(audience);

export const shouldShowDateFilter = (audience?: AudienceWithCondition) =>
  shouldShowPixelComponents(audience) || shouldShowPulseComponents(audience);

export const getAudienceAdditions = (audience?: AudienceOverviewQuery['audience']) => {
  if (audience?.condition === AudienceCondition.Segment) {
    return (audience?.parentAudience?.info as AudienceInfoWithAdditions)?.additions || [];
  }
  return (audience?.info as AudienceInfoWithAdditions)?.additions || [];
};

export const dateFilterFromDateRange = (dateRange: DateRange, matchType: MatchTypeValue) => {
  const startDateString = dateRange.start.toISOString(true).split('T')[0];
  const endDateString = dateRange.end.toISOString(true).split('T')[0];
  return {
    dateFilter: {
      field: matchType,
      operator: { between: { from: startDateString, to: endDateString } },
    },
  };
};

export interface AttributionSource {
  group: UtmGroup;
  name: string;
}

export const attributionSourceFilters = (attributionSource: AttributionSource): PersonFilterInput[] => {
  if (!attributionSource) return [];

  const params: string[] = [];

  const addParam = (group: UtmGroup, name: string) => {
    params.push(`${mapAttributions[group]}=${encodeURIComponent(name.trim())}`);
  };

  const mapAttributions: Record<string, string> = {
    [UtmGroup.Source]: 'utm_source',
    [UtmGroup.Campaign]: 'utm_campaign',
    [UtmGroup.Medium]: 'utm_medium',
  };
  if (attributionSource.group !== UtmGroup.SourceAndMedium) {
    addParam(attributionSource.group, attributionSource.name);
  } else {
    const names = attributionSource.name.split('/');
    if (names.length !== 2) return [];
    addParam(UtmGroup.Source, names[0]);
    addParam(UtmGroup.Medium, names[1]);
  }

  return params.map((param) => ({
    extendedStringFilter: {
      field: FilterExtendedStringField.PixelHitUrl,
      operator: { contains: param },
    },
  }));
};

const audiencesConditionsThatSupportLookalikes = [
  AudienceCondition.CustomerList,
  AudienceCondition.Pixel,
  AudienceCondition.Pulse,
];

export const shouldShowLookalikes = (audienceData: AudienceWithCondition | AudienceOverviewQuery['audience']) =>
  audiencesConditionsThatSupportLookalikes.includes(audienceData?.condition) ||
  audiencesConditionsThatSupportLookalikes.includes(audienceData?.parentAudience?.condition);

export const getRangeDefault = (audienceData: AudienceOverviewQuery) => {
  // don't use default date for Global audiences or Customer List audiences
  if (shouldShowGlobalComponents(audienceData?.audience) || shouldShowCustomerListComponents(audienceData?.audience))
    return undefined;

  if (shouldShowPixelComponents(audienceData?.audience)) {
    return moment.range(moment().subtract(27, 'days'), moment());
  }

  const audienceAdditions = getAudienceAdditions(audienceData?.audience);
  const lastAddition = audienceAdditions?.at(-1);
  if (lastAddition) {
    return moment.range(moment(lastAddition.date).startOf('day'), moment(lastAddition.date).endOf('day'));
  }

  return undefined;
};

export interface ChartTypesByGroup {
  [groupKey: string]: { [propertyKey: string]: string };
}
// UserTaxonomySettingsManager: manages user-global taxonomy settings in localStorage
export type UserTaxonomySettings = {
  chartTypesByGroup: ChartTypesByGroup;
};

const getUserTaxonomySettingsKey = (userId: string) => `${visitIQTaxonomyUserSettings}-${userId}`;

export const UserTaxonomySettingsManager = {
  load(userId: string): UserTaxonomySettings {
    const raw = localStorage.getItem(getUserTaxonomySettingsKey(userId));
    if (!raw) {
      return { chartTypesByGroup: {} };
    }
    try {
      return JSON.parse(raw);
    } catch {
      return { chartTypesByGroup: {} };
    }
  },
  save(userId: string, settings: UserTaxonomySettings) {
    localStorage.setItem(getUserTaxonomySettingsKey(userId), JSON.stringify(settings));
  },

  getChartType(userId: string, groupKey: string, propertyKey: string): string | undefined {
    return this.load(userId).chartTypesByGroup?.[groupKey]?.[propertyKey];
  },
  setChartType(userId: string, groupKey: string, propertyKey: string, chartType: string) {
    const settings = this.load(userId);
    if (!settings.chartTypesByGroup[groupKey]) settings.chartTypesByGroup[groupKey] = {};
    settings.chartTypesByGroup[groupKey][propertyKey] = chartType;
    this.save(userId, settings);
  },

  getPropertiesByGroup(personProperties?: TaxonomyProperty[]): PropertyGroup[] {
    return PROPERTY_GROUPS_DEFINITIONS.map((group) => {
      return {
        ...group,
        properties: personProperties.filter((property) => {
          // Check if the property is in the group's properties array
          return group.properties.includes(property.key as FilterStringField | FilterNumberField);
        }),
      };
    });
  },
};

export const DUMMY_DAYS_TO_KEEP = 1;
export const DUMMY_HISTORICAL_DATA_SYNC = false;

export const shouldShowDaysToKeep = (audienceData: AudienceOverviewQuery) => {
  const isSegment = audienceData?.audience?.condition === AudienceCondition.Segment;
  const audienceCondition = audienceData?.audience?.condition;
  const parentCondition = audienceData?.audience?.parentAudience?.condition;
  const conditionToCheck = isSegment ? parentCondition : audienceCondition;
  const conditionsNotAllowingDaysToKeep = [AudienceCondition.CustomerList, AudienceCondition.Lookalike];
  return !conditionsNotAllowingDaysToKeep.includes(conditionToCheck);
};

export const pixelCadenceToString = (pixelCadence: PixelMatchingCadence) => {
  if (pixelCadence === PixelMatchingCadence.FourHours) {
    return i18next.t('common.every4Hours');
  }
  return i18next.t('common.every24Hours');
};
